import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/**
 * Blocks the app with a modal if the currently logged-in user must re-agree to a
 * newer version of the Terms of Service. Only the modal blocks interaction —
 * public pages (Landing, Browse, Listing detail) still render underneath.
 */
export default function ReconsentGate() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setStatus(null); return; }
    api.get("/legal/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [user]);

  const needs = status?.needs_reconsent === true;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/legal/reconsent", { document_type: "tos" });
      toast.success("Thanks for re-confirming");
      const { data } = await api.get("/legal/status");
      setStatus(data);
      setAgreed(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!needs} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onEscapeKeyDown={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl">Our Terms have changed</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-neutral-700">
          <p>
            We updated the DenShare Terms of Service (
            <span className="font-mono text-xs bg-neutral-100 px-1.5 py-0.5">{status?.tos?.agreed_version || "—"} → {status?.tos?.current_version}</span>).
            Please review and re-agree to keep using your account.
          </p>
          <label className="flex items-start gap-2 border border-neutral-200 p-3 cursor-pointer">
            <Checkbox
              data-testid="reconsent-checkbox"
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              className="mt-0.5 border-neutral-400 data-[state=checked]:bg-neutral-900 data-[state=checked]:border-neutral-900"
            />
            <span>
              I have read and agree to the updated{" "}
              <Link to="/terms" target="_blank" rel="noreferrer" className="underline text-neutral-900">Terms of Service</Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer" className="underline text-neutral-900">Privacy Policy</Link>.
            </span>
          </label>
          <div className="flex justify-end">
            <Button
              data-testid="reconsent-submit"
              onClick={submit}
              disabled={!agreed || busy}
              className="h-10 bg-neutral-900 hover:bg-neutral-800 disabled:opacity-40"
            >
              {busy ? "Saving..." : "Agree & continue"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
